import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';

import OverLayLoader from '../OverLayLoader';
import LightTooltip from '../LightTooltip';
import LayoutTooltip from '../LayoutTooltip';
import InfoIcon from '../InfoIcon';
import HighlightedInput from '../HighlightedInput';

import { lemmatize } from '../../services';
import { CONSTANT_KEYS } from '../../helpers/tableKeys';
import { createErrorNotification } from '../../utils/createErrorNotification';
import { languageOption } from '../../helpers/options';

const {
  CODE,
  WORDS,
  LANGUAGE,
  LANGUAGE_ID,
  NAME,
  LANGUAGE_NAME,
  WORD_AFTER_CLEANING,
} = CONSTANT_KEYS;

const SAMPLE_INPUT = 'sample_input';

const AddKeywordsForm = ({
  currentLanguage,
  onSubmit,
  onCancel,
  tableData,
  codeId,
}) => {
  const [data, setData] = useState({
    [LANGUAGE]: { name: languageOption[0], id: languageOption[0] },
    [CODE]: '',
    [NAME]: '',
    [SAMPLE_INPUT]: '',
  });
  const [words, setWords] = useState('');
  const [isWordsCorrect, setIsWordsCorrect] = useState(false);
  const [isLemm, setIsLemm] = useState(false);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation(['diagnoses', 'promptbar', 'common']);

  const sampleSchema = Yup.object().shape({
    [SAMPLE_INPUT]: Yup.string()
      .matches(/^(?=.*[A-Za-zА-Яа-я])/, t('promptbar:incorrectFormat'))
      .required(t('promptbar:requiredField')),
  });

  const wordsSchema = Yup.object().shape({
    [WORDS]: Yup.string()
      .matches(/^[a-zA-Zа-яА-Я0-9\s]*$/, t('promptbar:incorrectFormat'))
      .required(t('promptbar:requiredField')),
  });

  useEffect(() => {
    if (!tableData || !currentLanguage?.id) {
      return;
    }

    const localizedName = tableData?.names?.find(
      el => el[LANGUAGE_ID] === currentLanguage.id,
    );

    setData(prev => ({
      ...prev,
      ...tableData,
      [LANGUAGE]: currentLanguage,
      [NAME]: localizedName?.[NAME] ?? tableData?.[NAME] ?? '',
      [SAMPLE_INPUT]: '',
    }));
  }, [currentLanguage, tableData]);

  const removeError = name => {
    if (!error[name]) {
      return;
    }

    const tmpError = { ...error };
    delete tmpError[name];
    setError(tmpError);
  };

  const handleChange = event => {
    const { value, name } = event.target;
    removeError(name);
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleValidate = async nextData => {
    try {
      await sampleSchema.validate(nextData, { abortEarly: false });
    } catch (errors) {
      const validationError = {};
      errors.inner.forEach(validationItem => {
        validationError[validationItem.path] = validationItem.message;
      });
      setError(prev => ({ ...prev, ...validationError }));
    }
  };

  const handleLemmatize = async () => {
    try {
      setIsLoading(true);
      setError({});

      const isFormValid = await sampleSchema.isValid({
        [SAMPLE_INPUT]: data[SAMPLE_INPUT].trim(),
      });

      if (!isFormValid) {
        return;
      }

      const requestData = {
        [WORDS]: [data[SAMPLE_INPUT]],
        [LANGUAGE_NAME]: currentLanguage.name,
      };

      const result = await lemmatize(requestData);
      if (result) {
        wordsSchema
          .validate(
            {
              [WORDS]: result.data[0][WORD_AFTER_CLEANING],
            },
            { abortEarly: false },
          )
          .then(() => setIsWordsCorrect(true))
          .catch(validationResult => {
            const validationError = {};
            validationResult.inner.forEach(validationItem => {
              validationError[validationItem.path] = validationItem.message;
            });
            setIsWordsCorrect(false);
            setError(prev => ({ ...prev, ...validationError }));
          });

        setWords(result.data[0]);
        setIsLemm(true);
      }
    } catch (requestError) {
      createErrorNotification();
      console.log(requestError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = event => {
    event.preventDefault();

    if (!codeId) {
      createErrorNotification();
      return;
    }

    const newData = {
      wordset: words[WORD_AFTER_CLEANING],
      sample: data[SAMPLE_INPUT],
      ordered: false,
      language_id: currentLanguage.id,
      code_ids: [codeId],
    };

    onSubmit(newData);
  };

  return (
    <>
      {isLoading && <OverLayLoader />}
      {data && (
        <Box component="form" autoComplete="off">
          <FormControl fullWidth variant="standard">
            <InputLabel htmlFor="TextField-0" shrink={false}>
              {t('common:dictionaryLanguage')}
            </InputLabel>
            <TextField
              id="TextField-0"
              disabled
              name={LANGUAGE}
              value={data[LANGUAGE]?.name?.toUpperCase()}
            />
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
            <InputLabel htmlFor="TextField-1" shrink={false}>
              {t('table.code')}
            </InputLabel>
            <TextField
              id="TextField-1"
              disabled
              name={CODE}
              value={data[CODE]}
            />
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
            <InputLabel htmlFor="TextField-2" shrink={false}>
              {t(`table.name_${data[LANGUAGE].name}`)}
            </InputLabel>
            <TextField
              disabled
              id="TextField-2"
              name={NAME}
              value={data[NAME] || ''}
            />
          </FormControl>

          <FormControl
            fullWidth
            variant="standard"
            sx={{ mt: '2.5rem' }}
            error={Boolean(error[SAMPLE_INPUT])}
          >
            <InputLabel htmlFor="TextField-3" shrink={false}>
              {t(`table.sample_${data[LANGUAGE].name}`)}
              <LightTooltip
                title={<LayoutTooltip lang={data[LANGUAGE]?.name} />}
                arrow
                placement="right"
              >
                <InfoIcon />
              </LightTooltip>
            </InputLabel>
            <HighlightedInput
              value={data[SAMPLE_INPUT]}
              onChange={handleChange}
              lang={data[LANGUAGE]?.name}
              minRows={1}
              inputProps={{
                id: 'TextField-3',
                autoFocus: true,
                name: SAMPLE_INPUT,
                onBlur: () => handleValidate(data),
                onKeyDown: keyEvent => {
                  if (keyEvent.key === 'Enter') {
                    keyEvent.preventDefault();
                    handleLemmatize();
                  }
                },
              }}
            />
            {error[SAMPLE_INPUT] && (
              <FormHelperText>{error[SAMPLE_INPUT]}</FormHelperText>
            )}

            <Button
              type="button"
              variant="contained"
              disabled={!data[SAMPLE_INPUT].trim() || error[SAMPLE_INPUT]}
              sx={{ display: 'flex', mt: '2rem', mx: 'auto' }}
              onClick={handleLemmatize}
            >
              {t('promptbar:lemmatize')}
            </Button>
          </FormControl>

          {isLemm && (
            <Box sx={{ mt: '2.5rem' }}>
              <FormControl
                fullWidth
                variant="standard"
                error={Boolean(error[WORDS])}
              >
                <InputLabel htmlFor="TextField-4" shrink={false}>
                  {t(`table.words_${currentLanguage.name}`)}
                </InputLabel>
                <TextField
                  disabled
                  id="TextField-4"
                  name="words"
                  value={words?.[WORD_AFTER_CLEANING] ?? ''}
                  helperText={error[WORDS] ?? null}
                  error={Boolean(error[WORDS])}
                />
              </FormControl>

              {words?.nondict && (
                <FormControl fullWidth variant="standard" sx={{ mt: 5 }}>
                  <InputLabel htmlFor="TextField-5" shrink={false}>
                    {t('Non-dictionary words', { ns: 'common' })}
                  </InputLabel>
                  <TextField
                    disabled
                    id="TextField-5"
                    name="nondict"
                    value={words.nondict || ''}
                    helperText={t(
                      'Add these words to the dictionary of replacements',
                      {
                        ns: 'promptbar',
                      },
                    )}
                    error={true}
                  />
                </FormControl>
              )}

              <Box
                sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}
              >
                <Button
                  type="button"
                  color="secondary"
                  sx={{ mr: 1 }}
                  variant="contained"
                  onClick={onCancel}
                >
                  {t('promptbar:cancel')}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleFormSubmit}
                  disabled={
                    !isLemm ||
                    !words?.[WORD_AFTER_CLEANING]?.trim() ||
                    !isWordsCorrect ||
                    Boolean(words?.nondict)
                  }
                >
                  {t('promptbar:save')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

AddKeywordsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  tableData: PropTypes.object,
  codeId: PropTypes.number,
  currentLanguage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
};

export default AddKeywordsForm;
